import React from "react";
import { Link, Redirect } from "react-router-dom";
import API from "../../API/REST_API";

export default class Home extends React.Component {
  state = {
    isCheckingAuth: true,
    shouldRedirect: false,
  };

  async componentDidMount() {
    // If already know user is logged in, redirect immediately
    if (this.props.user?.logedIn) {
      this.setState({ shouldRedirect: true, isCheckingAuth: false });
      return;
    }

    // Check if user has valid session by calling /me endpoint
    try {
      const userUrl = `${this.props.baseUrl}/api/v1/users/me`;
      const user = await API.Get(userUrl);

      if (user && user.id) {
        if (this.props.loginValidate) {
          await this.props.loginValidate();
        }

        await new Promise(resolve => setTimeout(resolve, 100));
        this.setState({ shouldRedirect: true, isCheckingAuth: false });
      } else {
        this.setState({ isCheckingAuth: false });
      }
    } catch (error) {
      this.setState({ isCheckingAuth: false });
    }
  }

  render() {
    const { isCheckingAuth, shouldRedirect } = this.state;
    const isLoggedIn = this.props.user?.logedIn;

    if (shouldRedirect || isLoggedIn) {
      return <Redirect to="/dashboard" />;
    }

    if (isCheckingAuth) {
      return (
        <div className="flex h-screen items-center justify-center bg-white">
          <div className="text-slate-600">
            <svg
              className="h-8 w-8 animate-spin text-slate-900"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white">
        <section className="bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
          <div className="px-4 py-16 md:px-8 md:py-24">
            <div className="max-w-2xl text-left">
              <div className="text-sm font-semibold uppercase tracking-wide text-slate-300">
                Kakeibo Method
              </div>
              <h1 className="mt-3 text-3xl font-semibold text-white md:text-5xl">
                Budgeting that feels calm, clear, and consistent.
              </h1>
              <p className="mt-4 text-sm text-slate-200 md:text-base">
                Capture income, plan expenses, and review progress in a simple
                workflow that builds real habits.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center rounded-md bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-100"
                >
                  Create Account
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center rounded-md border border-white/30 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10"
                >
                  Sign In
                </Link>
              </div>
              <div className="mt-6 flex flex-wrap gap-4 text-xs text-slate-300">
                <div>Privacy-first</div>
                <div>Mobile friendly</div>
                <div>Fast setup</div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 py-12 md:px-8 md:py-16">
          <div className="grid gap-6 md:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="text-base font-semibold text-slate-900">
                Simple by design
              </div>
              <p className="mt-2 text-sm text-slate-600">
                Track income, expenses, and savings without noise or clutter.
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="text-base font-semibold text-slate-900">
                Mindful habits
              </div>
              <p className="mt-2 text-sm text-slate-600">
                Review spending and build healthier choices over time.
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="text-base font-semibold text-slate-900">
                Clear goals
              </div>
              <p className="mt-2 text-sm text-slate-600">
                Set targets and see progress with simple reports.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-slate-50">
          <div className="px-4 py-12 md:px-8 md:py-16">
            <div className="grid gap-8 md:grid-cols-2 md:items-center">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 md:text-2xl">
                  A focused flow for everyday money decisions
                </h2>
                <p className="mt-3 text-sm text-slate-600">
                  Kakeibo guides you to plan before you spend and reflect after
                  you do. The result is clarity, not complexity.
                </p>
                <div className="mt-6 space-y-3 text-sm text-slate-700">
                  <div className="rounded-md border border-slate-200 bg-white p-3">
                    1. Plan monthly income and fixed costs
                  </div>
                  <div className="rounded-md border border-slate-200 bg-white p-3">
                    2. Track daily spending in categories
                  </div>
                  <div className="rounded-md border border-slate-200 bg-white p-3">
                    3. Review reports and adjust goals
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="text-sm font-semibold text-slate-900">
                  Quick Start
                </div>
                <div className="mt-3 space-y-2 text-sm text-slate-600">
                  <div className="flex items-center justify-between">
                    <span>Setup time</span>
                    <span className="font-semibold text-slate-900">2 min</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Core sections</span>
                    <span className="font-semibold text-slate-900">4</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Reports</span>
                    <span className="font-semibold text-slate-900">Live</span>
                  </div>
                </div>
                <Link
                  to="/register"
                  className="mt-6 inline-flex w-full items-center justify-center rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                >
                  Get Started
                </Link>
              </div>
            </div>
          </div>
        </section>

        <footer className="border-t border-slate-200 bg-white px-4 py-6 text-center text-xs text-slate-400">
          <Link to="/privacy" className="hover:text-slate-600 underline">
            Privacy Policy
          </Link>
          <span className="mx-2">&middot;</span>
          <Link to="/support" className="hover:text-slate-600 underline">
            Support
          </Link>
          <span className="mx-2">&middot;</span>
          &copy; {new Date().getFullYear()} Kakeibo
        </footer>
      </div>
    );
  }
}
